import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  List,
  Grid3X3,
  X
} from 'lucide-react';
import api from '../utils/api';
import ProductCard from './ProductCard';
import ExportModal from './ExportModal';
import ProgressToast from './ProgressToast';
import { exportToExcel, generateDefaultFilename } from '../utils/exportToExcel';

interface GridViewProps {
  tableName: string;
  hideFilters?: boolean;
  filters?: any;
  displayName?: string;
  comparisonMode?: boolean;
  comparisonData?: any[];
  onRemoveFromComparison?: (productId: string) => void;
  onAddToComparison?: (product: any) => void;
  comparisonProducts?: any[];
  viewMode?: 'table' | 'grid';
  onViewModeChange?: (newViewMode: 'table' | 'grid') => void;
  onClearAll?: () => void;
  currentTableProducts?: any[]; // Productos disponibles en la toma actual
  currentTableInfo?: { name: string; date?: string }; // Información de la toma actual
  isTableTransitioning?: boolean; // Nueva prop para controlar la transición
}

const GridView: React.FC<GridViewProps> = ({
  tableName,
  hideFilters = false,
  filters: externalFilters,
  displayName,
  comparisonMode = false,
  comparisonData = [],
  onRemoveFromComparison,
  onAddToComparison,
  comparisonProducts = [],
  viewMode = 'grid',
  onViewModeChange,
  onClearAll,
  currentTableProducts = [],
  currentTableInfo,
  isTableTransitioning = false
}) => {
  const { t } = useTranslation();
  
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [, setError] = useState<string | null>(null);

  // Estados de paginación
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 24,
  });
  const [totalRows, setTotalRows] = useState(0);
  const [totalUnfilteredRows, setTotalUnfilteredRows] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  // Estado para precio de referencia
  const [precioReferencia, setPrecioReferencia] = useState('');
  
  // Estado del modal de exportación
  const [exportModalOpen, setExportModalOpen] = useState(false);
  
  // Estados para el progress toast
  const [exportProgress, setExportProgress] = useState(0);
  const [exportMessage, setExportMessage] = useState('');
  const [showExportProgress, setShowExportProgress] = useState(false);
  const [exportComplete, setExportComplete] = useState(false);

  // Función para verificar si un producto está en comparación
  const isProductInComparison = (product: any): boolean => {
    // En modo comparación, todos los productos mostrados están en comparación
    if (comparisonMode) {
      return true;
    }
    
    if (!comparisonProducts || comparisonProducts.length === 0) {
      return false;
    }
    
    // Lógica simplificada:
    // 1. Si el producto tiene 'id' (demo), comparar por id
    // 2. Si no tiene 'id' (clientes activos), comparar por id_catalogo
    
    const isInComparison = comparisonProducts.some(comparisonProduct => {
      let matches = false;
      
      if (product.id && comparisonProduct.id) {
        // Ambos son productos demo - comparar por ID único
        matches = String(product.id) === String(comparisonProduct.id);
        
        if (matches) {
        }
      } else if (!product.id && !comparisonProduct.id) {
        // Ambos son productos de clientes activos - comparar por id_catalogo
        const productIdCatalogo = product.id_catalogo || product.ID_CATALOGO;
        const comparisonProductIdCatalogo = comparisonProduct.id_catalogo || comparisonProduct.ID_CATALOGO;
        
        if (productIdCatalogo && comparisonProductIdCatalogo) {
          matches = String(productIdCatalogo) === String(comparisonProductIdCatalogo);
          
          if (matches) {
          }
        }
      }
      
      // Si hay match, copiar el comparisonId
      if (matches && comparisonProduct.comparisonId && !product.comparisonId) {
        product.comparisonId = comparisonProduct.comparisonId;
      }
      
      return matches;
    });
    
    return isInComparison;
  };

  // Cargar datos de la tabla
  const loadTableData = async () => {
    
    if (comparisonMode) {
      
      // Verificar si los productos tienen comparisonId
      setData(comparisonData);
      setTotalRows(comparisonData.length);
      setTotalUnfilteredRows(comparisonData.length);
      const comparisonPages = comparisonData.length > 0 ? Math.ceil(comparisonData.length / pagination.pageSize) : 1;
      setTotalPages(comparisonPages);
      setLoading(false);
      return;
    }

    if (!tableName) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams();
      params.append('limit', pagination.pageSize.toString());
      params.append('offset', (pagination.pageIndex * pagination.pageSize).toString());
      
      // Aplicar filtros externos si existen
      if (externalFilters) {
        Object.keys(externalFilters).forEach(key => {
          const value = externalFilters[key];
          if (value !== undefined && value !== null && value !== '') {
            // Si el valor es un objeto, convertirlo a JSON string
            if (typeof value === 'object') {
              params.append(key, JSON.stringify(value));
            } else {
              params.append(key, String(value));
            }
          }
        });
      }
      
      
      // Log específico para debug de categorías
      const catFilter = params.get('Cat');
      const categoriaFilter = params.get('categoria');
      if (catFilter || categoriaFilter) {
      } else {
      }
      
      const response = await api.get(`/api/tables/${tableName}/data?${params}`);
      const result = response.data;

      // T-1 (rediseño): tomas no visibles se filtran antes de la lista; el gate
      // defensivo responde 403. No hay estado "procesando" en la UI.

      // DEBUG INFO FROM SERVER
      if (result._DEBUG_INFO) {
      }
      
      // Debug: verificar si los datos incluyen campos de buybox
      if (result.data && result.data.length > 0) {
      }
      
      // Debug: verificar valores de paginación
      
      setData(result.data || []);
      setTotalRows(result.total || 0);
      setTotalUnfilteredRows(result.totalUnfiltered || 0);
      // Calcular páginas basándose en total y pageSize para evitar división por 0
      const calculatedPages = result.total > 0 ? Math.ceil(result.total / pagination.pageSize) : 0;
      setTotalPages(result.pages || result.totalPages || calculatedPages || 1);
      
    } catch (error) {
      setError(t('errors.loading'));
    } finally {
      setLoading(false);
    }
  };

  // BUG-FIX (2026-06-05): unificar los 2 useEffects que llamaban loadTableData
  // en paralelo (tableName/pagination + filtros externos). Antes disparaban
  // 2 fetches por cambio de tabla → 4 con StrictMode dev. Ahora un solo effect
  // con todas las deps relevantes.
  useEffect(() => {
    if (isTableTransitioning) return;
    if (comparisonMode) {
      loadTableData();
      return;
    }
    if (tableName) {
      loadTableData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    tableName,
    comparisonMode,
    pagination.pageIndex,
    pagination.pageSize,
    isTableTransitioning,
    comparisonData.length,
    externalFilters?.global_search,
    externalFilters?.Marca,
    externalFilters?.sitio,
    externalFilters?.categoria,
    externalFilters?.Cat,
    externalFilters?.price_from,
    externalFilters?.price_to,
    externalFilters?.max_page,
    externalFilters?.max_order,
    externalFilters?.descuento_filter,
  ]);

  // Función auxiliar para cargar variantes de buybox
  const loadBuyboxVariantsForProducts = async (products: any[], updateProgress?: (msg: string) => void) => {
    
    const productsWithVariants: any[] = [];
    let _variantsLoaded = 0;
    let _productsWithBuyboxCount = 0;
    const MAX_CONCURRENT_REQUESTS = 5;
    
    // Procesar productos en lotes
    const processBatch = async (batch: any[]) => {
      const promises = batch.map(async (product) => {
        const result = {
          product,
          variants: [] as any[]
        };
        
        const buyboxId = product.buybox_id || product.BUYBOX_ID || product.BuyBox_ID;
        const productId = product.id_catalogo || product.ID_CATALOGO || product.id;
        
        if (buyboxId && buyboxId !== '(NULL)' && buyboxId !== 'NULL') {
          _productsWithBuyboxCount++;
          try {
            const response = await api.get(`/api/tables/${tableName}/buybox/${buyboxId}`);
            const variants = response.data.variants || [];

            result.variants = variants.map((variant: any) => ({
              ...variant,
              is_buybox_variant: true,
              parent_id_catalogo: productId
            }));
            _variantsLoaded += variants.length;
          } catch (error) {
            // Error silencioso al cargar variantes
          }
        }
        
        return result;
      });
      
      return Promise.all(promises);
    };
    
    // Procesar productos en lotes
    for (let i = 0; i < products.length; i += MAX_CONCURRENT_REQUESTS) {
      const batch = products.slice(i, i + MAX_CONCURRENT_REQUESTS);
      const results = await processBatch(batch);
      
      // Agregar productos y sus variantes al resultado
      results.forEach(({ product, variants }) => {
        productsWithVariants.push(product);
        productsWithVariants.push(...variants);
      });
      
      // Actualizar progreso si es necesario
      if (updateProgress && i % 20 === 0 && products.length > 100) {
        const progress = Math.round((i / products.length) * 100);
        updateProgress(`Cargando variantes de BuyBox... ${progress}%`);
      }
    }
    
    
    return productsWithVariants;
  };

  // Función para manejar la exportación
  const handleExport = async (filename: string, includeBuyboxVariants?: boolean) => {
    // Crear columnas completas para la exportación desde GridView
    const columns = [
      { id: 'imagen', columnDef: { header: t('columns.image'), accessorKey: 'imagen' }, getIsVisible: () => true },
      { id: 'titulo', columnDef: { header: t('columns.title'), accessorKey: 'titulo' }, getIsVisible: () => true },
      { id: 'marca', columnDef: { header: t('columns.brand'), accessorKey: 'marca' }, getIsVisible: () => true },
      { id: 'sitio', columnDef: { header: t('columns.site'), accessorKey: 'sitio' }, getIsVisible: () => true },
      { id: 'categoria', columnDef: { header: t('columns.category'), accessorKey: 'categoria' }, getIsVisible: () => true },
      { id: 'pagina_orden', columnDef: { 
        header: 'PAG : ORD', 
        accessorFn: (row: any) => ({
          pagina: row.page || row.pagina || 0,
          orden: row.orderInPage || row.orden_en_pag || row.orden || 0
        })
      }, getIsVisible: () => true },
      { id: 'precio', columnDef: { header: t('columns.original_price'), accessorKey: 'original_price' }, getIsVisible: () => true },
      { id: 'precio_desc', columnDef: { header: t('columns.discounted_price'), accessorKey: 'discounted_price' }, getIsVisible: () => true },
      { id: 'precio_venta', columnDef: { header: t('columns.sale_price'), accessorKey: 'sale_price' }, getIsVisible: () => true },
      { id: 'cuotas', columnDef: { header: t('columns.installments'), accessorKey: 'installments' }, getIsVisible: () => true },
      { id: 'precio_cuota', columnDef: { header: t('columns.installment_value'), accessorKey: 'installments_value' }, getIsVisible: () => true },
    ];
    
    // Si es modo comparación, usar los datos de comparación
    if (comparisonMode && comparisonData) {
      let dataToExport = comparisonData;
      
      if (includeBuyboxVariants) {
        setShowExportProgress(true);
        setExportProgress(20);
        setExportMessage('Cargando variantes de BuyBox...');
        dataToExport = await loadBuyboxVariantsForProducts(comparisonData, setExportMessage);
        setExportProgress(80);
        setExportMessage(`Variantes cargadas. Total de registros: ${dataToExport.length.toLocaleString()}`);
      }
      
      exportToExcel({
        columns,
        data: dataToExport,
        filename,
        includeBuyboxVariants
      });
      
      if (includeBuyboxVariants) {
        setExportProgress(100);
        setExportMessage('¡Exportación completada con variantes de BuyBox!');
        setExportComplete(true);
        setTimeout(() => {
          setShowExportProgress(false);
        }, 2000);
      }
      
      return;
    }
    
    // Para modo normal, descargar TODOS los registros
    try {
      // Inicializar progress toast
      setExportProgress(0);
      setExportComplete(false);
      setShowExportProgress(true);
      setExportMessage('Preparando exportación...');
      
      // Simular progreso inicial
      setTimeout(() => setExportProgress(5), 100);
      
      // BUG-005 FIX (2026-06-05): unificar serialización de filtros con
      // loadTableData (línea ~163). Antes:
      //   - Arrays → `.join('|')` (rompía filtros array que el backend espera como JSON).
      //   - Objetos no-array → `String(value) = '[object Object]'` (rompe search_operators).
      // Ahora: objects → JSON.stringify, scalars → String. Mismo patrón que loadTableData.
      const appendFilters = (target: URLSearchParams) => {
        if (!externalFilters) return;
        Object.entries(externalFilters).forEach(([key, value]) => {
          if (value === undefined || value === null || value === '') return;
          if (typeof value === 'object') {
            target.append(key, JSON.stringify(value));
          } else {
            target.append(key, String(value));
          }
        });
      };

      // Primero obtener el conteo total con una petición pequeña
      const countParams = new URLSearchParams();
      countParams.append('limit', '1'); // Solo necesitamos 1 registro para obtener el total
      countParams.append('offset', '0');
      appendFilters(countParams);

      setExportProgress(10);
      setExportMessage('Obteniendo información de registros...');

      const countResponse = await api.get(`/api/tables/${tableName}/data?${countParams}`);
      const totalRecords = countResponse.data.total || 0;

      // BUG-005 FIX (2026-06-05): defensive check vs grid totalRows.
      if (totalRows > 0 && totalRecords > 0 && Math.abs(totalRecords - totalRows) / totalRows > 0.1) {
        console.warn('[export] count mismatch entre grid y export', {
          grid_total: totalRows,
          export_count: totalRecords,
          filters: externalFilters,
          tableName,
        });
      }

      setExportMessage(`Preparando descarga de ${totalRecords.toLocaleString()} registros...`);
      setExportProgress(15);

      let allData: any[] = [];

      // Para datasets pequeños (< 50k), descargar todo de una vez
      if (totalRecords <= 50000) {
        const params = new URLSearchParams();
        params.append('limit', totalRecords.toString());
        params.append('offset', '0');
        appendFilters(params);
        
        setExportProgress(20);
        setExportMessage(`Descargando ${totalRecords.toLocaleString('es-AR')} productos del servidor...`);
        
        const response = await api.get(`/api/tables/${tableName}/data?${params}`, {
          onDownloadProgress: (progressEvent: any) => {
            if (progressEvent.total) {
              const percentCompleted = Math.round((progressEvent.loaded * 60) / progressEvent.total) + 20;
              setExportProgress(percentCompleted);
            }
          }
        });
        
        allData = response.data.data || [];
      } else {
        // Para datasets grandes (> 50k), descargar en chunks
        const CHUNK_SIZE = 25000;
        const chunks = Math.ceil(totalRecords / CHUNK_SIZE);
        
        setExportMessage(`Descargando ${totalRecords.toLocaleString()} registros en ${chunks} partes...`);
        
        for (let i = 0; i < chunks; i++) {
          const chunkParams = new URLSearchParams();
          chunkParams.append('limit', CHUNK_SIZE.toString());
          chunkParams.append('offset', (i * CHUNK_SIZE).toString());
          appendFilters(chunkParams);
          
          const progressBase = 20 + (i * 60 / chunks);
          const progressEnd = 20 + ((i + 1) * 60 / chunks);
          
          setExportMessage(`Descargando parte ${i + 1} de ${chunks}...`);
          setExportProgress(progressBase);
          
          try {
            const response = await api.get(`/api/tables/${tableName}/data?${chunkParams}`, {
              onDownloadProgress: (progressEvent: any) => {
                if (progressEvent.total) {
                  const chunkProgress = (progressEvent.loaded / progressEvent.total);
                  const overallProgress = progressBase + (chunkProgress * (progressEnd - progressBase));
                  setExportProgress(Math.round(overallProgress));
                }
              }
            });
            
            const chunkData = response.data.data || [];
            allData = allData.concat(chunkData);
            
            // Pequeña pausa entre chunks para no sobrecargar
            if (i < chunks - 1) {
              await new Promise(resolve => setTimeout(resolve, 100));
            }
          } catch (chunkError) {
            console.error(`Error al descargar chunk ${i + 1}:`, chunkError);
            setExportMessage(`Error en parte ${i + 1}, continuando...`);
            // Continuar con el siguiente chunk
          }
        }
      }
      
      if (allData.length > 0) {
        let dataToExport = allData;
        
        if (includeBuyboxVariants) {
          setExportProgress(70);
          setExportMessage('Cargando variantes de BuyBox...');
          dataToExport = await loadBuyboxVariantsForProducts(allData, setExportMessage);
          setExportMessage(`Variantes cargadas. Total de registros: ${dataToExport.length.toLocaleString()}`);
        }
        
        setExportProgress(85);
        setExportMessage(`Procesando ${dataToExport.length.toLocaleString()} registros...`);
        
        // Pequeña pausa para mostrar el mensaje
        await new Promise(resolve => setTimeout(resolve, 500));
        
        exportToExcel({
          columns,
          data: dataToExport,
          filename,
          includeBuyboxVariants
        });
        
        setExportProgress(100);
        setExportMessage(`¡Exportación completada! ${dataToExport.length.toLocaleString()} registros`);
        setExportComplete(true);
      } else {
        throw new Error('No se pudieron descargar los datos');
      }
    } catch (error) {
      console.error('Error al exportar todos los datos:', error);
      setShowExportProgress(false);
      // En caso de error, exportar solo los datos actuales
      let dataToExport = data;
      
      if (includeBuyboxVariants && data.length > 0) {
        dataToExport = await loadBuyboxVariantsForProducts(data);
      }
      
      exportToExcel({
        columns,
        data: dataToExport,
        filename,
        includeBuyboxVariants
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Información de registros y precio de referencia */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-600">
          {t('table.showing')} {pagination.pageIndex * pagination.pageSize + 1} - {Math.min((pagination.pageIndex + 1) * pagination.pageSize, totalRows)} {t('table.of')} {totalRows} {t('table.records')}
          {totalPages > 0 && (
            <span className="text-gray-400 ml-2">
              · {t('table.page')} {pagination.pageIndex + 1} {t('table.of')} {totalPages}
            </span>
          )}
          {totalUnfilteredRows > totalRows && (
            <span className="text-blue-600 ml-2">
              ({t('table.filtered_from')} {totalUnfilteredRows.toLocaleString()} {t('table.records')})
            </span>
          )}
        </div>
        
        {/* Campo de precio de referencia y controles de vista */}
        <div className="flex items-center space-x-4">
          {/* Precio de referencia */}
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-gray-700 whitespace-nowrap">{t('table.reference_price')}:</label>
              <div className="flex items-center space-x-1 relative">
                <span className="text-sm text-gray-500">$</span>
                <input
                  type="number"
                  value={precioReferencia}
                  onChange={(e) => setPrecioReferencia(e.target.value)}
                  placeholder="0.00"
                  className="w-32 px-3 py-1 pr-8 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                {precioReferencia && (
                  <button
                    onClick={() => setPrecioReferencia('')}
                    className="absolute right-2 text-gray-400 hover:text-gray-600 text-sm font-bold"
                    title={t('table.clear_reference_price')}
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>
            
            {precioReferencia && (
              <div className="flex items-center space-x-2 text-xs text-gray-500">
                <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                <span>{t('table.lower')}</span>
                <span className="w-2 h-2 bg-cyan-500 rounded-full"></span>
                <span>{t('table.equal')}</span>
                <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                <span>{t('table.higher')}</span>
              </div>
            )}
          </div>

          {/* Controles de vista */}
          {onViewModeChange && (
            <div className="flex items-center space-x-4">
              {/* Selector de vista */}
              <div className="flex bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => onViewModeChange('table')}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    viewMode === 'table'
                      ? 'bg-white text-purple-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                  title={t('view.switch_to_table')}
                >
                  <List className="h-4 w-4" />
                  <span>{t('view.table')}</span>
                </button>
                <button
                  onClick={() => onViewModeChange('grid')}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    viewMode === 'grid'
                      ? 'bg-white text-purple-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                  title={t('view.switch_to_grid')}
                >
                  <Grid3X3 className="h-4 w-4" />
                  <span>{t('view.grid')}</span>
                </button>
              </div>
              
              {/* Botón de exportar */}
              <button
                onClick={() => setExportModalOpen(true)}
                className="flex items-center gap-1 px-3 py-2 text-green-600 hover:text-green-700 border border-green-300 rounded-lg hover:bg-green-50 transition-colors"
                title={t('export.export_to_excel', 'Exportar a Excel')}
              >
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20M12.5,10A0.5,0.5 0 0,1 13,10.5V13H15.5V14.5H13V17A0.5,0.5 0 0,1 12.5,17.5A0.5,0.5 0 0,1 12,17V14.5H9.5V13H12V10.5A0.5,0.5 0 0,1 12.5,10M8.5,12A0.5,0.5 0 0,1 9,12.5V17A0.5,0.5 0 0,1 8.5,17.5A0.5,0.5 0 0,1 8,17V12.5A0.5,0.5 0 0,1 8.5,12M15.5,12A0.5,0.5 0 0,1 16,12.5V17A0.5,0.5 0 0,1 15.5,17.5A0.5,0.5 0 0,1 15,17V12.5A0.5,0.5 0 0,1 15.5,12Z"/>
                </svg>
                <span className="text-sm font-medium">XLS</span>
              </button>
              
              {/* Botón limpiar todo - solo en modo comparación */}
              {comparisonMode && onClearAll && (
                <button
                  onClick={onClearAll}
                  className="flex items-center space-x-2 px-3 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  <X className="h-4 w-4" />
                  <span className="text-sm">{t('compare.clear_all')}</span>
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Grilla de productos */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="animate-spin h-6 w-6 text-blue-500 mr-2" />
            {t('table.loading')}
          </div>
        ) : data.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            {t('table.no_data')}
          </div>
        ) : (
          <div 
            className="overflow-x-auto"
            style={{
              scrollbarWidth: 'thin',
              scrollbarColor: '#CBD5E0 #F7FAFC'
            }}
          >
            <style dangerouslySetInnerHTML={{
              __html: `
                .grid-view-container::-webkit-scrollbar {
                  height: 8px;
                }
                .grid-view-container::-webkit-scrollbar-track {
                  background: #F7FAFC;
                  border-radius: 4px;
                }
                .grid-view-container::-webkit-scrollbar-thumb {
                  background: #CBD5E0;
                  border-radius: 4px;
                }
                .grid-view-container::-webkit-scrollbar-thumb:hover {
                  background: #A0AEC0;
                }
              `
            }} />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 grid-view-container" style={{ minWidth: '600px' }}>
              {data.map((product, index) => {
                // Generar una key única considerando el modo comparación
                const uniqueKey = comparisonMode && product.comparisonId 
                  ? product.comparisonId 
                  : `${product.id_catalogo || product.ID_CATALOGO || product.id || index}_${product.originalTableName || tableName}_${index}`;
                
                // Si no estamos en modo comparación, buscar si este producto está en comparación
                // para pasarle el comparisonId correcto
                let productToRender = product;
                if (!comparisonMode && comparisonProducts && comparisonProducts.length > 0) {
                  const comparisonProduct = comparisonProducts.find(cp => {
                    // Usar la misma lógica de comparación
                    if (product.id && cp.id) {
                      return String(product.id) === String(cp.id);
                    } else if (!product.id && !cp.id) {
                      const productIdCatalogo = product.id_catalogo || product.ID_CATALOGO;
                      const cpIdCatalogo = cp.id_catalogo || cp.ID_CATALOGO;
                      return productIdCatalogo && cpIdCatalogo && String(productIdCatalogo) === String(cpIdCatalogo);
                    }
                    return false;
                  });
                  
                  if (comparisonProduct && comparisonProduct.comparisonId) {
                    // Crear una copia del producto con el comparisonId
                    productToRender = { ...product, comparisonId: comparisonProduct.comparisonId };
                  }
                }
                
                return (
                  <ProductCard
                    key={uniqueKey}
                    product={productToRender}
                    onAddToComparison={onAddToComparison}
                    onRemoveFromComparison={onRemoveFromComparison}
                    comparisonProducts={comparisonProducts}
                    isInComparison={isProductInComparison(product)}
                    precioReferencia={precioReferencia ? parseFloat(precioReferencia) : undefined}
                    comparisonMode={comparisonMode}
                    currentTableProducts={currentTableProducts}
                    isFromSavedGroup={comparisonMode}
                    tableName={tableName}
                    currentTableInfo={currentTableInfo}
                  />
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Paginación */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-700">{t('table.rows_per_page')}:</span>
          <select
            value={pagination.pageSize}
            onChange={(e) => {
              setPagination({
                ...pagination,
                pageSize: Number(e.target.value),
                pageIndex: 0,
              });
            }}
            className="border border-gray-300 rounded px-3 py-1 text-sm"
          >
            {[12, 24, 48, 96].map((pageSize) => (
              <option key={pageSize} value={pageSize}>
                {pageSize}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center space-x-2">
          <button
            onClick={() => setPagination({ ...pagination, pageIndex: 0 })}
            disabled={pagination.pageIndex === 0}
            className="p-2 text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronsLeft className="h-4 w-4" />
          </button>
          <button
            onClick={() => setPagination({ ...pagination, pageIndex: pagination.pageIndex - 1 })}
            disabled={pagination.pageIndex === 0}
            className="p-2 text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          
          <span className="text-sm text-gray-700 px-4">
            {t('table.page')} {pagination.pageIndex + 1} {t('table.of')} {Math.max(totalPages, 1)}
          </span>
          
          <button
            onClick={() => setPagination({ ...pagination, pageIndex: pagination.pageIndex + 1 })}
            disabled={pagination.pageIndex >= totalPages - 1}
            className="p-2 text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
          <button
            onClick={() => setPagination({ ...pagination, pageIndex: totalPages - 1 })}
            disabled={pagination.pageIndex >= totalPages - 1}
            className="p-2 text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronsRight className="h-4 w-4" />
          </button>
        </div>
      </div>
      
      {/* Modal de exportación */}
      <ExportModal
        isOpen={exportModalOpen}
        onClose={() => setExportModalOpen(false)}
        onExport={handleExport}
        defaultFilename={generateDefaultFilename(tableName, totalRows, currentTableInfo?.date)}
      />
      
      {/* Progress Toast para exportación */}
      <ProgressToast
        isOpen={showExportProgress}
        onClose={() => setShowExportProgress(false)}
        progress={exportProgress}
        message={exportMessage}
        isComplete={exportComplete}
        totalRows={totalRows}
      />
    </div>
  );
};

export default GridView; 